<template>
  <div class="jadwal-daftar">
    <section class="section">
      <div class="app-container">
        <header class="section__head section__head--center" v-reveal>
          <span class="eyebrow">{{ $t('scheduleIndex.eyebrow') }}</span>
          <h1 class="section-title">{{ $t('scheduleIndex.title') }}</h1>
          <p class="section-subtitle" style="margin-inline: auto">
            {{ $t('scheduleIndex.subtitle') }}
          </p>
        </header>

        <!--
          Kolom cari muncul hanya kalau acaranya sudah cukup banyak.
          Kotak pencarian di atas daftar berisi dua baris hanya menambah
          benda yang harus dibaca tanpa menolong siapa pun.
        -->
        <div v-if="semua.length > 4" class="jadwal-daftar__cari">
          <i class="bi bi-search" aria-hidden="true"></i>
          <input
            v-model.trim="kata"
            type="search"
            class="ui-input"
            :placeholder="$t('scheduleIndex.searchPlaceholder')"
            :aria-label="$t('scheduleIndex.searchPlaceholder')"
          />
        </div>

        <!-- Memuat -->
        <div v-if="memuat" class="jadwal-daftar__grid">
          <div v-for="n in 3" :key="n" class="ui-card jadwal-kartu">
            <div class="ui-card__body stack">
              <div class="ui-skeleton" style="height: 20px; width: 45%"></div>
              <div class="ui-skeleton" style="height: 26px; width: 90%"></div>
              <div class="ui-skeleton" style="height: 16px; width: 65%"></div>
              <div class="ui-skeleton" style="height: 44px; margin-top: 8px"></div>
            </div>
          </div>
        </div>

        <!-- Gagal -->
        <div v-else-if="galat" class="ui-empty">
          <span class="ui-empty__icon"><i class="bi bi-wifi-off" aria-hidden="true"></i></span>
          <p class="ui-empty__title">{{ $t('home.loadFailed') }}</p>
          <p>{{ galat }}</p>
          <button type="button" class="ui-btn ui-btn--secondary" style="margin-top: 1rem" @click="ambilData">
            <i class="bi bi-arrow-clockwise" aria-hidden="true"></i> {{ $t('common.retry') }}
          </button>
        </div>

        <!-- Kosong -->
        <div v-else-if="!terlihat.length" class="ui-empty">
          <span class="ui-empty__icon"><i class="bi bi-calendar-x" aria-hidden="true"></i></span>
          <p class="ui-empty__title">
            {{ kata ? $t('scheduleIndex.noMatch') : $t('scheduleIndex.empty') }}
          </p>
          <p>{{ kata ? $t('scheduleIndex.noMatchDesc') : $t('scheduleIndex.emptyDesc') }}</p>
          <button
            v-if="kata"
            type="button"
            class="ui-btn ui-btn--secondary"
            style="margin-top: 1rem"
            @click="kata = ''"
          >
            {{ $t('scheduleIndex.clearSearch') }}
          </button>
        </div>

        <!-- Penyaring: acara + jenis pertandingan -->
        <div v-else class="jadwal-saring" v-reveal>
          <div class="jadwal-saring__baris">
            <label class="ui-field jadwal-saring__acara">
              <span class="ui-label">{{ $t('scheduleIndex.filterEvent') }}</span>
              <select v-model="acaraTerpilih" class="ui-input">
                <option v-for="t in terlihat" :key="t.id" :value="t.slug">
                  {{ t.name }}
                </option>
              </select>
            </label>

            <div class="ui-field">
              <span class="ui-label">{{ $t('scheduleIndex.filterType') }}</span>
              <!--
                Dua tombol, bukan dropdown. Hanya ada dua pilihan dan
                keduanya sering ditukar-tukar; dropdown menambah satu
                ketukan untuk sesuatu yang bisa terlihat sekaligus.
              -->
              <div class="jadwal-saring__jenis" role="group">
                <button
                  type="button"
                  class="ui-btn"
                  :class="jenis === 'tanding' ? 'ui-btn--primary' : 'ui-btn--secondary'"
                  @click="jenis = 'tanding'"
                >
                  <i class="bi bi-person-arms-up" aria-hidden="true"></i>
                  {{ $t('schedule.tanding') }}
                </button>
                <button
                  type="button"
                  class="ui-btn"
                  :class="jenis === 'seni' ? 'ui-btn--primary' : 'ui-btn--secondary'"
                  @click="jenis = 'seni'"
                >
                  <i class="bi bi-stars" aria-hidden="true"></i>
                  {{ $t('schedule.seni') }}
                </button>
              </div>
            </div>
          </div>

          <p v-if="acaraAktif" class="jadwal-saring__acara-info">
            <i class="bi bi-geo-alt" aria-hidden="true"></i>
            {{ acaraAktif.location || '-' }}
            <span v-if="acaraAktif.event_date"> &middot; {{ acaraAktif.event_date }}</span>
          </p>
        </div>

        <!--
          Jadwalnya dipasang di halaman ini, bukan di alamat terpisah.
          `key` memaksa komponen dibuat ulang saat acara atau jenisnya
          berganti — tanpa itu, keadaan penyaring di dalam tabel (arena,
          tanggal, babak) terbawa dari acara sebelumnya dan hasilnya
          tampak seperti jadwal yang salah.
        -->
        <div v-if="acaraTerpilih" class="jadwal-isi">
          <ScheduleTandingPage
            v-if="jenis === 'tanding'"
            :key="`tanding-${acaraTerpilih}`"
            :slug="acaraTerpilih"
          />
          <ScheduleSeniPage
            v-else
            :key="`seni-${acaraTerpilih}`"
            :slug="acaraTerpilih"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
/**
 * Daftar jadwal seluruh acara.
 *
 * KENAPA HALAMAN INI ADA
 * Tombol "Lihat Jadwal" di beranda dulu melompat langsung ke jadwal acara
 * sorotan. Selama acaranya cuma satu itu terasa benar; begitu ada beberapa,
 * pengunjung yang mencari acara lain sampai di jadwal yang keliru — dan
 * halaman jadwal tidak menyediakan cara berpindah acara, jadi jalan
 * satu-satunya adalah menekan tombol kembali.
 *
 * Alamatnya (/schedule) juga bisa dibagikan ke grup pelatih tanpa terikat
 * pada satu acara tertentu.
 *
 * MENGGANTIKAN APA
 * Alamat ini sebelumnya menampilkan tabel jadwal dengan sederet penyaring,
 * tetapi id acaranya dibaca dari `?tournament_id=` di URL. Dibuka lewat
 * menu — tanpa parameter itu — halaman memanggil
 * `/tournaments/undefined/match-schedules` dan selalu kosong. Tidak ada
 * satu pun tautan di situs yang mengirim parameter tersebut.
 */
import http from '@/services/http';
import { infoStatus, hanyaTerlihat, sedangBerlangsung } from '@/utils/status';
import ScheduleTandingPage from '@/main/pages/ScheduleTandingPage.vue';
import ScheduleSeniPage from '@/main/pages/ScheduleSeniPage.vue';

export default {
  name: 'SchedulePage',

  components: { ScheduleTandingPage, ScheduleSeniPage },

  data() {
    return {
      semua: [],
      memuat: true,
      galat: null,
      kata: '',
      acaraTerpilih: '',
      jenis: 'tanding',
    };
  },

  computed: {
    acaraAktif() {
      return this.semua.find((t) => t.slug === this.acaraTerpilih) || null;
    },

    terlihat() {
      const k = this.kata.toLowerCase();
      if (!k) return this.semua;

      return this.semua.filter((t) =>
        `${t.name || ''} ${t.location || ''}`.toLowerCase().includes(k)
      );
    },
  },

  mounted() {
    document.title = this.$t('scheduleIndex.title') || 'Jadwal Pertandingan';
    this.ambilData();
  },

  watch: {
    /*
     * Pilihan disimpan di alamat, bukan hanya di memori.
     * Dengan begitu pelatih bisa membagikan tautan jadwal acara tertentu,
     * dan menekan muat-ulang tidak melempar mereka kembali ke acara
     * pertama. replace dipakai, bukan push, supaya tombol Kembali tidak
     * dipenuhi riwayat setiap kali penyaring digeser.
     */
    acaraTerpilih() { this.simpanKeAlamat(); },
    jenis() { this.simpanKeAlamat(); },
  },

  methods: {
    infoStatus,

    simpanKeAlamat() {
      if (!this.acaraTerpilih) return;
      this.$router.replace({
        query: { acara: this.acaraTerpilih, jenis: this.jenis },
      }).catch(() => {});
    },


    async ambilData() {
      this.memuat = true;
      this.galat = null;

      try {
        /*
         * Memakai endpoint publik `all`, bukan `active`.
         * Jadwal acara yang sudah selesai tetap dicari orang — untuk melihat
         * hasil dan urutan partai tahun lalu. Yang benar-benar disembunyikan
         * hanya acara berstatus "sembunyikan", dan itu sudah disaring oleh
         * hanyaTerlihat().
         */
        const { data } = await http.get('/tournaments/all');
        const daftar = Array.isArray(data) ? data : data?.data ?? [];

        this.semua = this.urutkan(hanyaTerlihat(daftar));

        /*
         * Acara pertama dipilih otomatis — urutannya sudah menaruh yang
         * sedang berlangsung di depan. Membiarkan penyaring kosong berarti
         * pengunjung disambut halaman hampa dan harus menebak bahwa ada
         * yang perlu dipilih dulu.
         *
         * Alamat ?acara=<slug> dihormati supaya tautan bisa dibagikan.
         */
        const dariAlamat = this.$route.query.acara;
        const sah = this.semua.some((t) => t.slug === dariAlamat);
        this.acaraTerpilih = sah ? dariAlamat : (this.semua[0]?.slug || '');

        if (['tanding', 'seni'].includes(this.$route.query.jenis)) {
          this.jenis = this.$route.query.jenis;
        }
      } catch (error) {
        this.galat = error.friendlyMessage || this.$t('home.loadFailed');
        this.semua = [];
      } finally {
        this.memuat = false;
      }
    },

    /**
     * Urutan: yang sedang berlangsung dulu, lalu yang paling baru.
     *
     * Mengurutkan murni berdasarkan tanggal akan menenggelamkan acara yang
     * partainya sedang berjalan hari ini di bawah acara yang tanggalnya
     * lebih baru — padahal justru acara itu yang jadwalnya sedang dicari.
     */
    urutkan(daftar) {
      return [...daftar].sort((a, b) => {
        const ba = sedangBerlangsung(a) ? 0 : 1;
        const bb = sedangBerlangsung(b) ? 0 : 1;
        if (ba !== bb) return ba - bb;

        const ta = new Date(a.start_date || 0).getTime() || 0;
        const tb = new Date(b.start_date || 0).getTime() || 0;
        return tb - ta;
      });
    },
  },
};
</script>

<style scoped>
/* ── Penyaring jadwal ── */
.jadwal-saring {
  max-width: 900px;
  margin: 0 auto var(--space-8);
  padding: var(--space-5);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
}

.jadwal-saring__baris {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 720px) {
  .jadwal-saring__baris { grid-template-columns: 2fr 1fr; align-items: end; }
}

.jadwal-saring__acara .ui-input { width: 100%; }

/* Dua tombol selebar sama. Di layar sempit tombol yang ukurannya
   mengikuti isi bisa menyusut sampai sulit disentuh. */
.jadwal-saring__jenis {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2);
}

.jadwal-saring__acara-info {
  margin: var(--space-4) 0 0;
  font-size: var(--text-sm);
  color: var(--text-secondary);
}

.jadwal-saring__acara-info i { color: var(--accent); }

/* Halaman jadwal yang dipasang di dalam sudah punya jarak atasnya
   sendiri; tanpa penyesuaian ini jaraknya jadi dobel. */
.jadwal-isi :deep(.section) { padding-block-start: 0; }

.jadwal-daftar__cari {
  position: relative;
  max-width: 460px;
  margin: 0 auto var(--space-8);
}

.jadwal-daftar__cari i {
  position: absolute;
  left: var(--space-4);
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.jadwal-daftar__cari .ui-input { padding-left: var(--space-10); }

.jadwal-daftar__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 640px)  { .jadwal-daftar__grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .jadwal-daftar__grid { grid-template-columns: repeat(3, 1fr); } }

/* Isi kartu dibuat kolom penuh supaya tombolnya bisa didorong ke dasar
   kartu — tanpa itu, kartu dengan nama acara pendek punya tombol yang
   melayang di tengah dan barisnya terlihat tidak rata. */
.jadwal-kartu__isi {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.jadwal-kartu__judul {
  margin: var(--space-3) 0;
  font-size: var(--text-lg);
  line-height: 1.3;
}

.jadwal-kartu__judul a { color: var(--text-primary); }
.jadwal-kartu__judul a:hover { color: var(--text-brand); }

.jadwal-kartu__meta {
  list-style: none;
  margin: 0 0 var(--space-5);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-secondary);
}

.jadwal-kartu__meta li { display: flex; align-items: flex-start; gap: var(--space-2); }
.jadwal-kartu__meta i { color: var(--accent); margin-top: 2px; flex-shrink: 0; }

/*
 * Dua tombol sejajar, masing-masing selebar setengah kartu.
 * Di layar 360px tombol berdampingan yang lebarnya mengikuti isi bisa
 * menyusut sampai sulit disentuh; 1fr 1fr menjaga keduanya tetap lebar.
 */
.jadwal-kartu__aksi {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2);
  margin-top: auto;
}
</style>
